import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Phone, Mail, Calendar, CheckCircle2, PlayCircle, Activity, Info,X } from "lucide-react";
import { Link } from "react-router-dom";

const Endoscopic = () => {
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    // Show the popup 1 second after page load
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);
  return (
    <div className="min-h-screen bg-[#f8fafc] pb-20">
      
      {/* --- VIDEO POPUP --- */}
      {showPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="relative w-full max-w-4xl bg-white rounded-3xl overflow-hidden shadow-2xl border-4 border-[#800000]">
            {/* Close Button */}
            <button 
              onClick={() => setShowPopup(false)}
              className="absolute top-4 right-4 z-50 bg-[#800000] hover:bg-red-900 p-2 rounded-full text-white shadow-lg transition-transform hover:scale-110"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Video Wrapper */}
            <div className="aspect-video bg-black">
              <iframe 
                width="100%" 
                height="100%" 
                src="https://www.youtube.com/embed/juqqo7PQWEs?autoplay=1" 
                title="YouTube video player" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section follows as normal... */}
      
      {/* Hero Section */}
      <section className="relative bg-[#800000] text-white py-20 px-6 overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-white/5 skew-x-12 transform translate-x-20" />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="inline-flex items-center space-x-2 bg-white/20 px-4 py-1.5 rounded-full mb-6 border border-white/30">
            <Activity className="w-4 h-4 text-white" />
            <span className="text-sm font-medium text-white">
              Big Bariatrics
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6">
            Endoscopic Sleeve Gastroplasty
          </h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 -mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">

        {/* Sidebar */}
        <aside className="lg:col-span-3 space-y-6">
          <Card className="sticky top-6 shadow-xl border-none bg-white/90 backdrop-blur-md">
            <CardHeader className="border-b border-gray-100">
              <CardTitle className="text-lg font-bold text-[#800000] flex items-center gap-2">
                <Info className="w-5 h-5 text-[#800000]" /> Patient Portal
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-2">
              <div className="mt-4 p-4 bg-[#800000] rounded-2xl text-white shadow-lg">
                <h4 className="font-bold mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> For Appointment
                </h4>
                <p className="text-xs text-white/80 mb-4">
                  Schedule your private assessment today.
                </p>
                <Link to="/contact">
                <Button className="w-full bg-white text-[#800000] hover:bg-gray-100 font-bold mt-2">
                  Reserve Now
                </Button>
                </Link>
                <div className="mt-4 pt-4 border-t border-white/20 space-y-2 text-xs">
                  <div className="flex items-center gap-2">
                    <Phone className="w-3 h-3" /> +91-755-4055123
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-3 h-3" /> contact@bigbariatrics.com
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </aside>

        {/* Main Content */}
        <main className="lg:col-span-9 space-y-12">

          {/* Procedure Section */}
          <section className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-gray-100">
            <div className="flex flex-col md:flex-row gap-12 items-center">
              <div className="flex-1 space-y-6 text-gray-700">
                <p className="text-xl leading-relaxed">
                  Endoscopic Sleeve Gastroplasty (ESG) is a minimally invasive weight-loss procedure performed using an endoscope — a flexible tube passed through the mouth into the stomach. There are no cuts or scars on the abdomen.
                </p>
                <p className="text-lg leading-relaxed">
                  The stomach size is reduced using internal sutures, helping you feel full earlier and eat less. It is a suitable option for patients looking for a non-surgical weight-loss solution with quicker recovery.
                </p>
                <div className="w-full bg-gray-100 rounded-2xl overflow-hidden shadow-inner">
                  <img 
                    src="/endo-01.jpg" 
                    alt="Endoscopic Procedure"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="bg-[#ffe5e5] border-l-4 border-[#800000] p-4 italic rounded-r-lg">
                  ESG is a restrictive procedure, meaning it reduces stomach size but does not significantly affect hunger hormones. 
                  Long-term success depends on maintaining a healthy lifestyle and diet.
                </p>

                {/* Benefits */}
                <div>
                  <h4 className="font-bold text-lg text-gray-900 mb-2">
                    Key Benefits
                  </h4>
                  <ul className="list-disc pl-5 space-y-1 text-gray-600">
                    <li>No external cuts or scars</li>
                    <li>Short hospital stay or same-day discharge</li>
                    <li>Faster recovery compared to surgery</li>
                    <li>Lower complication risk</li>
                  </ul>
                </div>

                {/* Who is it for */}
                <div>
                  <h4 className="font-bold text-lg text-gray-900 mb-2">
                    Who is it for?
                  </h4>
                  <ul className="list-disc pl-5 space-y-1 text-gray-600">
                    <li>Patients with mild to moderate obesity</li>
                    <li>Those seeking a non-surgical option</li>
                    <li>Patients not ready for bariatric surgery</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Recovery Section */}
          <section>
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Your Recovery Journey
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 rounded-3xl overflow-hidden shadow-lg border border-gray-100">
              <div className="bg-white p-8 text-center">
                <div className="w-12 h-12 bg-[#ffe5e5] text-[#800000] rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                  01
                </div>
                <h4 className="font-bold mb-2">Weeks 1-2</h4>
                <p className="text-sm text-gray-600">Clear liquids and healing phase</p>
              </div>
              <div className="bg-[#ffe5e5] p-8 text-center">
                <div className="w-12 h-12 bg-[#800000] text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                  02
                </div>
                <h4 className="font-bold mb-2">Weeks 3-4</h4>
                <p className="text-sm text-gray-600">Soft & pureed foods</p>
              </div>
              <div className="bg-white p-8 text-center">
                <div className="w-12 h-12 bg-[#ffe5e5] text-[#800000] rounded-full flex items-center justify-center mx-auto mb-4 font-bold">
                  03
                </div>
                <h4 className="font-bold mb-2">Month 2+</h4>
                <p className="text-sm text-gray-600">Healthy long-term diet</p>
              </div>
            </div>
          </section>

          {/* Video Section */}
          {/* <section className=" rounded-3xl p-1 text-white ">
            <h3 className="text-2xl font-bold mb-6">Educational Videos</h3>
          <div className="gap-6">
  
  

  <div className="relative aspect-video rounded-xl overflow-hidden"> */}

  <iframe width="580" height="315" src="https://www.youtube.com/embed/juqqo7PQWEs?si=G7s0VuDDcrtoEaL-" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin"
   
    allowFullScreen></iframe>
{/* </div>

   

</div>
          </section> */}

          {/* Checklist */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-emerald-50 border-emerald-100">
              <CardHeader>
                <CardTitle className="text-emerald-800 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" /> Pre-Op Checklist
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p>Medical evaluation</p>
                <p>Pre-op diet</p>
                <p>Stop smoking</p>
              </CardContent>
            </Card>

            <Card className="bg-orange-50 border-orange-100">
              <CardHeader>
                <CardTitle className="text-orange-800 flex items-center gap-2">
                  <Info className="w-5 h-5" /> Expert Support
                </CardTitle>
              </CardHeader>
              <CardContent>
                24/7 monitoring and nutrition guidance for safe weight loss.
              </CardContent>
            </Card>
          </div>

        </main>
      </div>
    </div>
  );
};

export default Endoscopic;